import React, { useState } from 'react';
import { Sparkles, FileText, Wand2, Loader2, Eye, Download, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { presentationTemplates } from '../data/mockData';
import PresentationPreview from './PresentationPreview';
import { toast } from '../hooks/use-toast';
import { generatePresentation } from '../lib/llmService';
import { defaultSettings } from '../data/mockData';

const PresentationGenerator = () => {
  const [prompt, setPrompt] = useState('');
  const [title, setTitle] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('business');
  const [slidesCount, setSlidesCount] = useState('6');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedSlides, setGeneratedSlides] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast({
        title: 'Error',
        description: 'Por favor, ingresa un prompt para generar la presentación.',
        variant: 'destructive'
      });
      return;
    }

    setIsGenerating(true);

    try {
      // Get settings from localStorage
      const savedSettings = localStorage.getItem('slidegenius_settings');
      const settings = savedSettings ? JSON.parse(savedSettings) : defaultSettings;

      const apiKey = settings.apiProvider === 'emergent' ? settings.emergentKey : settings.claudeKey;

      if (!apiKey) {
        toast({
          title: 'Configuración pendiente',
          description: 'Por favor, configura tu API Key en la sección de Ajustes.',
          variant: 'destructive'
        });
        setIsGenerating(false);
        return;
      }

      const slides = await generatePresentation(prompt, {
        apiProvider: settings.apiProvider,
        apiKey: apiKey,
        slidesCount: slidesCount,
        template: selectedTemplate,
        language: settings.language || 'es'
      });

      setGeneratedSlides(slides);
      setIsGenerating(false);
      setShowPreview(true);

      toast({
        title: '¡Presentación generada!',
        description: `Se han creado ${slides.length} slides exitosamente.`
      });
    } catch (error) {
      console.error('Generation error:', error);
      toast({
        title: 'Error de generación',
        description: error.message || 'Hubo un problema al generar la presentación.',
        variant: 'destructive'
      });
      setIsGenerating(false);
    }
  };

  const selectedTemplateData = presentationTemplates.find(t => t.id === selectedTemplate);

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Nueva Presentación</h1>
          <p className="text-gray-500 mt-1">Genera presentaciones profesionales con IA en segundos</p>
        </div>
        {generatedSlides && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPreview(true)}
              className="flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              Vista previa
            </Button>
            <Button
              className="bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Exportar PPT
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Panel - Input Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Prompt Section */}
          <Card className="border-gray-200 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-medium text-gray-800">
                <Sparkles className="w-5 h-5 text-teal-600" />
                Describe tu presentación
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title" className="text-sm font-medium text-gray-700">Título de la Presentación</Label>
                <Input
                  id="title"
                  placeholder="Ej: Propuesta Software de Gestión"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="mt-1.5 border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                />
              </div>

              <div>
                <Label htmlFor="prompt" className="text-sm font-medium text-gray-700">Prompt de Contenido</Label>
                <Textarea
                  id="prompt"
                  placeholder="Describe el contenido que deseas en tu presentación. Por ejemplo: 'Crear una presentación para proponer un software de gestión automatizada para una empresa de porcelanatos. Incluir módulos de inventario, facturación y logística. Destacar beneficios de automatización y ROI esperado.'"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="mt-1.5 min-h-[150px] border-gray-300 focus:border-teal-500 focus:ring-teal-500 resize-none"
                />
                <p className="text-xs text-gray-400 mt-2">
                  Tip: Sé específico sobre el tema, audiencia y puntos clave que quieres cubrir.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Configuration Section */}
          <Card className="border-gray-200 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-medium text-gray-800">
                <FileText className="w-5 h-5 text-teal-600" />
                Configuración
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700">Plantilla</Label>
                  <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                    <SelectTrigger className="mt-1.5 border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {presentationTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: template.color }}
                            />
                            {template.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700">Número de Slides</Label>
                  <Select value={slidesCount} onValueChange={setSlidesCount}>
                    <SelectTrigger className="mt-1.5 border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">4 slides (Corta)</SelectItem>
                      <SelectItem value="6">6 slides (Estándar)</SelectItem>
                      <SelectItem value="8">8 slides (Completa)</SelectItem>
                      <SelectItem value="10">10 slides (Extendida)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={isGenerating || !prompt.trim()}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white h-12 text-base font-medium transition-all duration-200 disabled:opacity-50"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Generando presentación...
              </>
            ) : (
              <>
                <Wand2 className="w-5 h-5 mr-2" />
                Generar Presentación con IA
              </>
            )}
          </Button>
        </div>

        {/* Right Panel - Template Info & Quick Preview */}
        <div className="space-y-6">
          {/* Selected Template Card */}
          <Card className="border-gray-200 shadow-sm overflow-hidden">
            <div
              className="h-2"
              style={{ backgroundColor: selectedTemplateData?.color || '#0D9488' }}
            />
            <CardContent className="pt-4">
              <h3 className="font-medium text-gray-900">{selectedTemplateData?.name}</h3>
              <p className="text-sm text-gray-500 mt-1">{selectedTemplateData?.description}</p>

              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Estructura sugerida</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-3 h-3 text-teal-600" />
                    Slide de título
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-3 h-3 text-teal-600" />
                    Introducción
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-3 h-3 text-teal-600" />
                    Características
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-3 h-3 text-teal-600" />
                    Cronograma
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-3 h-3 text-teal-600" />
                    Inversión
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-3 h-3 text-teal-600" />
                    Contacto
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Tips Card */}
          <Card className="border-gray-200 shadow-sm bg-teal-50/50">
            <CardContent className="pt-5">
              <h3 className="font-medium text-teal-800 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Tips para mejores resultados
              </h3>
              <ul className="mt-3 space-y-2 text-sm text-teal-700">
                <li>• Define claramente tu audiencia objetivo</li>
                <li>• Menciona los puntos clave a destacar</li>
                <li>• Indica el tono deseado (formal, casual)</li>
                <li>• Especifica datos o métricas importantes</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Presentation Preview Modal */}
      {showPreview && generatedSlides && (
        <PresentationPreview
          slides={generatedSlides}
          template={selectedTemplateData}
          title={title || 'Mi Presentación'}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};

export default PresentationGenerator;
